import React, { FC, useCallback, useEffect, useState } from 'react';
import { Form, Space, Typography } from 'antd';

import { useSelector } from 'react-redux';
import Text from 'antd/lib/typography/Text';
import TextArea from 'antd/es/input/TextArea';
import { ContainerWithForm, FormContainer } from '../../../components/Container';
import FooterNavigator from '../../../components/FooterNavigator';
import { PHN_GP_DETAILS, PHN_CONFIRMATION, PHN_PATIENT_DETAILS } from '../../../constants/routes';
import { useAppDispatch } from '../../../hooks/redux-hooks';
import {
  addAttachmentsData,
  selectBookingAttachmentsData, selectBookingQuestionAndAnswersData, updateClientHealthSummaryData,
} from '../../../redux/slices/booking';
import UploadForm from '../../../components/UploadForm';
import { removeEmptyStrFields } from '../../../utils/object-check';
import { getPrivateReferralDetailsConfig, getPrivateGpDetailsConfig } from '../../../config/privateBooking';
import { UPLOAD_STATUS } from '../../../model/Attachment';

const DocumentsAndMedicalInfo: FC = () => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const {
    healthSummary, medicalSupporting, additionalReport1, additionalReport2,
  } = useSelector(selectBookingAttachmentsData);
  const { healthConcern, gpName } = useSelector(selectBookingQuestionAndAnswersData);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleUploadHealthSummary = useCallback((attachment) => {
    dispatch(addAttachmentsData({ healthSummary: attachment }));
    setUploadError(null);
  }, [dispatch]);
  const handleUploadMedicalSupporting = useCallback((attachment) => dispatch(addAttachmentsData({ medicalSupporting: attachment })), [dispatch]);
  const handleUploadAdditionalReport1 = useCallback((attachment) => dispatch(addAttachmentsData({ additionalReport1: attachment })), [dispatch]);
  const handleUploadAdditionalReport2 = useCallback((attachment) => dispatch(addAttachmentsData({ additionalReport2: attachment })), [dispatch]);
  const {
    primaryPresentingProblem, currentMedication, mmhcCentreDetails,
    firstUploadLabel, firstUploadRequired, showFirstUploadNote,
  } = getPrivateReferralDetailsConfig();
  const { showGpDetailsPage } = getPrivateGpDetailsConfig();
  const nextPage = showGpDetailsPage ? PHN_GP_DETAILS : PHN_CONFIRMATION;
  useEffect(() => {
    // analytics.logPageViewEvent(UPLOAD_REFERRAL, {
    //   site: SITE_NAME,
    //   page_path: pathname,
    // });
    form.setFieldsValue({ healthConcern, gpName });
  }, [form, healthConcern, gpName]);

  const isHealthSummaryUploaded = healthSummary?.status === UPLOAD_STATUS.SUCCESS
    || healthSummary?.status === UPLOAD_STATUS.DONE;

  const submit = useCallback(async () => {
    try {
      if (firstUploadRequired && !isHealthSummaryUploaded) {
        setUploadError('Please upload the required document before continuing.');
        return false;
      }
      setUploadError(null);
      const values = await form.validateFields();

      dispatch(updateClientHealthSummaryData(removeEmptyStrFields(values)));
      return true;
    } catch (e) {
      // analytics.trackVpcFormError({
      //   form_id: bookingData.formId,
      //   error_type: e,
      //   page_path: pathname,
      // });
      return false;
    }
  }, [dispatch, form, firstUploadRequired, isHealthSummaryUploaded]);

  return (
    <ContainerWithForm>
      <Space direction="vertical" size={50}>
        <Typography.Title level={2} style={{ marginBottom: '20px', color: '#5f6369', fontSize: '36px' }}>
          Upload Patient Referral / Supporting Documents
        </Typography.Title>

        <FormContainer layout="vertical" form={form}>
          <Form.Item
            label={firstUploadLabel}
            required={firstUploadRequired}
            validateStatus={uploadError ? 'error' : ''}
            help={uploadError}
          >
            <UploadForm uploadInfo={healthSummary} setUploadInfo={handleUploadHealthSummary} />
            {showFirstUploadNote && (
              <>
                If you do not have a valid GP referral,
                <Text strong>{' a Medicare rebate will not be available.'}</Text>
              </>
            )}
          </Form.Item>

          <Form.Item
            label="If you have any medical supporting documents (GP care plan, mental health summary, historical psychiatric reports, allied health reports, and other medical summaries) please upload here:"
          >
            <UploadForm uploadInfo={medicalSupporting} setUploadInfo={handleUploadMedicalSupporting} />
          </Form.Item>

          <Form.Item
            label="If you have any other medical reports please upload here:"
          >
            <UploadForm uploadInfo={additionalReport1} setUploadInfo={handleUploadAdditionalReport1} />
          </Form.Item>

          <Form.Item
            label="If you have any other medical summaries please upload here:"
          >
            <UploadForm uploadInfo={additionalReport2} setUploadInfo={handleUploadAdditionalReport2} />
          </Form.Item>
          {primaryPresentingProblem && (
            <Form.Item
              name="primaryPresentingProblem"
              label="Primary Presenting Problem"
              rules={[
                {
                  required: true,
                  message: 'Please input the primary presenting problem.',
                },
              ]}
            >
              <TextArea rows={4} />
            </Form.Item>
          )}
          {currentMedication && (
            <Form.Item name="currentMedications" label="Current Medications">
              <TextArea rows={4} />
            </Form.Item>
          )}
          <Form.Item
            name="risksOrConcerns"
            label="Risks or concerns"
            rules={[
              {
                required: true,
                message: 'Please input the health risks and concerns.',
              },
            ]}
          >
            <TextArea rows={4} />
          </Form.Item>

          <Form.Item
            name="diagnosis"
            label="Diagnosis (if known)"
          >
            <TextArea rows={4} />
          </Form.Item>

          {mmhcCentreDetails && (
            <Form.Item
              name="mmhcCentreDetails"
              label="MMHC Centre details"
            >
              <TextArea rows={4} />
            </Form.Item>
          )}

          <Form.Item
            name="additionalInformation"
            label="Additional information"
          >
            <TextArea rows={4} />
          </Form.Item>

          <FooterNavigator
            submit={submit}
            error=""
            next={nextPage}
            back={PHN_PATIENT_DETAILS}
            containerStyle={{ marginTop: '50px' }}
          />
        </FormContainer>
      </Space>
    </ContainerWithForm>
  );
};

export default DocumentsAndMedicalInfo;
